import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useParams, Route, Routes } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Image,
  Modal,
  Switch,
  Box,
  Text,
  Flex,
  ActionIcon,
} from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { IconMail } from "@tabler/icons-react";

import {
  AvatarManagement,
  ErrorView,
  PageTitle,
  PhoneManagement,
  Subnav,
} from "@components/shared";
import { ManagerForm } from "@components/Manager";
import { PasswordForm } from "@components/Auth";
import { AssociateButton } from "@components/Manager/ManagerAssociate";
import { GeospotList } from "@components/Geospot";

import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";
import generateStaticMap from "@util/generateStaticMap";

export default function ManagerDetail({ isProfile, isAdmin }) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manager, setManager] = useState(null);
  const params = useParams();
  const { id } = params;

  const managerId = isProfile
    ? useSelector((state) => state.manager).manager_id
    : id;

  useEffect(() => {
    fetchData();
  }, []);

  function fetchData() {
    setLoading(true);
    axios
      .get(`/managers/${managerId}/`)
      .then(({ data }) => {
        setManager(data.response[0]);
      })
      .catch((err) => {
        setError(err);
        setManager(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  if (loading) {
    return null;
  }

  if (error) {
    return <ErrorView text={error} />;
  }

  const showLocations = manager.location_joins.length > 0;

  return (
    <div>
      <Group mb="sm">
        <AvatarManagement managerId={managerId} asset={manager.profile_asset} />
        <div>
          <PageTitle
            title={
              <Flex
                gap="xs"
                direction={{ base: "column", sm: "row" }}
                align="center"
              >
                <span>
                  {manager.first_name} {manager.last_name}
                </span>
                {isAdmin && manager.status === 1 && (
                  <AssociateButton
                    buttonText="Resend signup email"
                    buttonProps={{
                      size: "compact-sm",
                      leftSection: <IconMail size={18} />,
                      radius: "xl",
                      color: entityColor.manager,
                    }}
                    url="/resend-signup/"
                    onSuccess={() => true}
                    reqData={{
                      manager_id: id,
                    }}
                  />
                )}
              </Flex>
            }
            subtitle={"Manager"}
            icon={entityIcon.manager()}
            iconColor={entityColor.manager}
            style={{ marginBottom: "0" }}
          >
            <Box>
              <Flex gap="xs">
                {manager.logged_in === true && (
                  <Badge variant="light" color="green">
                    Logged In
                  </Badge>
                )}
                <Badge
                  variant="light"
                  color={
                    manager.status === 1
                      ? "yellow"
                      : manager.status === 2
                      ? "green"
                      : "red"
                  }
                >
                  {manager.status === 1
                    ? "Signup incomplete"
                    : manager.status === 2
                    ? "Active"
                    : "Inactive"}
                </Badge>
                {manager.logged_in === false && manager.status !== 4 && (
                  <Badge variant="light" color="gray">
                    Logged Out
                  </Badge>
                )}
                {manager.status === 4 && (
                  <Badge variant="light" color="gray" size="xs">
                    Disabled
                  </Badge>
                )}
              </Flex>
            </Box>
          </PageTitle>
        </div>
      </Group>
      <Subnav
        buttonColor={entityColor.manager}
        links={[
          {
            to: `/${isProfile ? "profile" : "managers"}/${managerId}`,
            text: "Detail ",
          },
          {
            to: `/${isProfile ? "profile" : "managers"}/${managerId}/locations`,
            text: "Locations",
          },
          // { to: `/users/${id}/interactions`, text: "Interactions" },
        ].filter((f) => (!showLocations ? f.text !== "Locations" : true))}
      />
      <Routes>
        <Route
          exact
          path="/"
          element={
            <>
              <Card>
                <ManagerForm
                  id={managerId}
                  email={manager.email}
                  firstName={manager.first_name}
                  lastName={manager.last_name}
                  mfa={manager.mfa}
                  onSuccess={fetchData}
                />
              </Card>
              <Card mt="lg">
                <PhoneManagement
                  managerId={managerId}
                  fetchEntity={fetchData}
                  mfaEnabled={manager.mfa}
                  showMfa={manager.email ? true : false}
                  email={manager.email}
                />
              </Card>
              {isProfile && <ChangePassword managerId={managerId} />}
              {isAdmin && manager.status !== 1 && (
                <ManagerStatus
                  managerId={managerId}
                  fetchData={fetchData}
                  status={manager.status}
                />
              )}
            </>
          }
        />
        {showLocations && (
          <Route
            path="locations"
            element={
              <>
                <GeospotList
                  editable={false}
                  items={manager.location_joins
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((m) => ({
                      ...m,
                      id: m.location_id,
                      children: (
                        <ActionIcon
                          component={Link}
                          size="xs"
                          to={`/locations/${m.location_id}`}
                          color="gray"
                          variant="light"
                          radius="xl"
                        >
                          <IconExternalLink size={14} />
                        </ActionIcon>
                      ),
                    }))}
                  fetchData={fetchData}
                />
                {/* <Grid>
                  {manager.location_joins.map((m) => (
                    <Grid.Col span={{ base: 12, md: 6 }} key={m.location_id}>
                      <Card component={Link} to={`/locations/${m.location_id}`}>
                        <Text fw={600} mb="sm">
                          {m.name}
                        </Text>
                        <Card.Section>
                          <Image
                            h={250}
                            mah={250}
                            fit="cover"
                            style={{
                              overflow: "hidden",
                            }}
                            src={generateStaticMap(
                              { latitude: m.latitude, longitude: m.longitude },
                              { width: 500, height: 250 }
                            )}
                          />
                        </Card.Section>
                      </Card>
                    </Grid.Col>
                  ))}
                </Grid> */}
              </>
            }
          />
        )}
      </Routes>
    </div>
  );
}

function ChangePassword({ managerId }) {
  const [isOpen, setOpen] = useState(false);

  return (
    <React.Fragment>
      <Button variant="light" size="xs" mt="xl" onClick={() => setOpen(true)}>
        Change Password
      </Button>
      <Modal
        opened={isOpen}
        onClose={() => setOpen(false)}
        title="Change Password"
      >
        <PasswordForm
          onSuccess={() => setOpen(false)}
          updating
          additionalReqData={{
            manager_id: managerId,
          }}
        />
      </Modal>
    </React.Fragment>
  );
}

function ManagerStatus({ managerId, fetchData, status }) {
  function changeStatus() {
    axios
      .get(`/managers/${managerId}/change-status/`)
      .then(() => {
        fetchData();
      })
      .catch((err) => {
        toast.error(err);
        fetchData();
      });
  }

  return (
    <React.Fragment>
      <Switch
        mt="xl"
        size="md"
        description={
          status === 2
            ? "Unchecking will disable this manager from logging in along with removing them from all locations & organizations."
            : "Checking will reactivate this manager allowing them to log in"
        }
        label="Activation Status"
        onClick={() => changeStatus()}
        checked={status === 2}
      />
    </React.Fragment>
  );
}
